/**
 * The IANA timezone list, grouped and searchable — V10 §4.
 *
 * Pure, and separate from the control that renders it, because the part worth
 * proving is the search: somebody types "kolk" or "dubai", not
 * "Asia/Kolkata". A picker that only matches the stored id is a free-text field
 * with extra clicks.
 */

/**
 * Enough of the world to fall back on.
 *
 * `Intl.supportedValuesOf` is the real source and has been for years, but it is
 * a runtime capability rather than a language one, and a control that renders
 * an empty list is worse than one that renders a short one.
 */
const FALLBACK_ZONES = [
  'UTC',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Australia/Sydney',
];

/**
 * Zones whose ICU name is not the one the person living there uses.
 *
 * `Intl.supportedValuesOf('timeZone')` returns IANA's *backward-compatible*
 * ids, so it offers `Asia/Calcutta` and not `Asia/Kolkata` — the founder's own
 * city, and the document's own example of what search must find. Every entry
 * below is a rename IANA made and ICU still reports the old side of; both names
 * remain valid identifiers, so displaying and storing the modern one is a
 * naming decision rather than a semantic one.
 *
 * Search matches either, because somebody who learned the old name should not
 * have to learn the new one to find themselves.
 */
const MODERN_NAMES: Record<string, string> = {
  'Africa/Asmera': 'Africa/Asmara',
  'America/Buenos_Aires': 'America/Argentina/Buenos_Aires',
  'America/Coral_Harbour': 'America/Atikokan',
  'America/Godthab': 'America/Nuuk',
  'Asia/Calcutta': 'Asia/Kolkata',
  'Asia/Katmandu': 'Asia/Kathmandu',
  'Asia/Rangoon': 'Asia/Yangon',
  'Asia/Saigon': 'Asia/Ho_Chi_Minh',
  'Atlantic/Faeroe': 'Atlantic/Faroe',
  'Europe/Kiev': 'Europe/Kyiv',
  'Pacific/Enderbury': 'Pacific/Kanton',
  'Pacific/Ponape': 'Pacific/Pohnpei',
  'Pacific/Truk': 'Pacific/Chuuk',
};

/** Modern name → the ICU name it replaced, for search only. */
const FORMER_NAMES: Record<string, string> = Object.fromEntries(
  Object.entries(MODERN_NAMES).map(([former, modern]) => [modern, former]),
);

/** Prefers the modern name, but only if this runtime actually accepts it. */
export function modernTimezoneName(zone: string): string {
  const modern = MODERN_NAMES[zone];
  if (!modern) return zone;
  try {
    new Intl.DateTimeFormat('en', { timeZone: modern });
    return modern;
  } catch {
    return zone;
  }
}

/** Every zone this runtime knows, modern-named, deduplicated and sorted. */
export function listTimezones(): string[] {
  const supported = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf;

  const zones = typeof supported === 'function' ? supported('timeZone') : FALLBACK_ZONES;
  return [...new Set(zones.map(modernTimezoneName))].sort((a, b) => a.localeCompare(b));
}

/** `Asia/Kolkata` → `Asia`. Zones with no slash (`UTC`) group under "Other". */
export function regionOf(zone: string): string {
  const slash = zone.indexOf('/');
  return slash === -1 ? 'Other' : zone.slice(0, slash);
}

/** `America/Argentina/Buenos_Aires` → `Buenos Aires`. */
export function cityOf(zone: string): string {
  const last = zone.slice(zone.lastIndexOf('/') + 1);
  return last.replace(/_/g, ' ');
}

/**
 * Matching on the city as well as the id.
 *
 * The founder's example is the whole requirement: "Kolkata" and "Dubai" are
 * what a person knows about themselves; `Asia/` is filing.
 */
export function matchesTimezone(zone: string, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;

  const former = FORMER_NAMES[zone];
  const haystacks = [
    zone,
    zone.replace(/_/g, ' '),
    cityOf(zone),
    regionOf(zone),
    ...(former ? [former, cityOf(former)] : []),
  ];
  return haystacks.some((value) => value.toLowerCase().includes(needle));
}

export function searchTimezones(zones: string[], query: string): string[] {
  return zones.filter((zone) => matchesTimezone(zone, query));
}

export interface TimezoneGroup {
  region: string;
  zones: string[];
}

/** Grouped by region, regions in alphabetical order, zones already sorted. */
export function groupTimezones(zones: string[]): TimezoneGroup[] {
  const byRegion = new Map<string, string[]>();
  for (const zone of zones) {
    const region = regionOf(zone);
    byRegion.set(region, [...(byRegion.get(region) ?? []), zone]);
  }
  return [...byRegion.entries()]
    .map(([region, list]) => ({ region, zones: list }))
    .sort((a, b) => a.region.localeCompare(b.region));
}

/**
 * The current time in a zone, for the preview beside each option.
 *
 * Returns an empty string rather than throwing for a zone this runtime does
 * not know — a stored legacy value should render as itself with no time beside
 * it, not blow up the control that exists to replace it.
 */
export function timeInZone(zone: string, at: Date = new Date()): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeZone: zone,
      hour: '2-digit',
      minute: '2-digit',
    }).format(at);
  } catch {
    return '';
  }
}

/** The browser's own guess, when there is nothing better to default to. */
export function browserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
}
